import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Globe, Linkedin, Mail, MapPin, Menu, Phone, X } from "lucide-react";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import { NAV, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

function LanguageToggle({ className = "" }) {
  const { lang, setLang, t } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`} role="group" aria-label={t("header.langLabel")} data-testid="language-toggle">
      <Globe className="mr-1 h-4 w-4 text-[#0F4C5C]" aria-hidden="true" />
      {["en", "es"].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
            lang === code ? "bg-[#0F4C5C] text-white" : "text-slate-500 hover:text-[#0F4C5C]"
          }`}
          data-testid={`lang-${code}-btn`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}

function TopBar() {
  const { t } = useLang();
  return (
    <div className="hidden bg-[#0F4C5C] text-white lg:block" data-testid="top-bar">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs sm:px-6 lg:px-8">
        <p className="hidden items-center gap-1.5 lg:flex" data-testid="top-bar-address">
          <MapPin className="h-3.5 w-3.5 text-[#E8A34C]" aria-hidden="true" />
          {SITE.address2}, {SITE.address3}
        </p>
        <p className="hidden items-center gap-1.5 md:flex lg:hidden" data-testid="top-bar-hours">
          {t("common.hoursShort")}
        </p>
        <div className="flex w-full items-center justify-center gap-4 sm:w-auto sm:justify-end">
          <a href={`mailto:${SITE.email}`} className="hidden items-center gap-1.5 hover:text-[#E8A34C] transition-colors duration-200 xl:flex" data-testid="top-bar-email">
            <Mail className="h-3.5 w-3.5" aria-hidden="true" /> {SITE.email}
          </a>
          <a href={`mailto:${SITE.emailDiana}`} className="hidden items-center gap-1.5 hover:text-[#E8A34C] transition-colors duration-200 xl:flex" data-testid="top-bar-email-diana">
            <Mail className="h-3.5 w-3.5" aria-hidden="true" /> {SITE.emailDiana}
          </a>
          <span className="hidden items-center gap-2.5 border-l border-white/20 pl-4 lg:flex">
            <a href="https://www.facebook.com/AFInsuranceGroup/" target="_blank" rel="noopener noreferrer" aria-label="A&F Insurance Group on Facebook" className="hover:text-[#E8A34C] transition-colors duration-200" data-testid="top-bar-facebook">
              <Facebook className="h-3.5 w-3.5" />
            </a>
            <a href="https://www.linkedin.com/in/af-insurance-group-inc-83499140/" target="_blank" rel="noopener noreferrer" aria-label="A&F Insurance Group on LinkedIn" className="hover:text-[#E8A34C] transition-colors duration-200" data-testid="top-bar-linkedin">
              <Linkedin className="h-3.5 w-3.5" />
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();
  useEffect(() => setOpen(false), [pathname]);

  const navLinkClass = ({ isActive }) =>
    `relative whitespace-nowrap px-1 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 ${
      isActive ? "text-[#0F4C5C]" : "text-slate-600 hover:text-[#0F4C5C]"
    } after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#D97706] after:transition-[width] after:duration-300 ${
      isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#0F4C5C]/10 bg-white" data-testid="main-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label={t("header.homeAria")}>
          <img src="/assets/logo.jpeg" alt="A&F Insurance Group, Inc. logo" className="h-14 w-auto sm:h-16" width="180" height="64" />
        </Link>
        <nav className="hidden items-center gap-4 lg:flex" aria-label={t("header.primaryNav")} data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink key={item.path} to={item.path} className={navLinkClass} data-testid={`nav-${item.key}`}>
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LanguageToggle className="hidden md:flex" />
          <div className="hidden flex-col gap-1 xl:flex" data-testid="header-phones">
            <a
              href={SITE.phone1Href}
              className="flex items-center gap-2 whitespace-nowrap text-base font-extrabold text-[#0F4C5C] hover:text-[#0284C7] transition-colors duration-200"
              data-testid="header-phone-link"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EEF6FC]">
                <Phone className="h-4 w-4" aria-hidden="true" />
              </span>
              {t("header.armandoLabel")} {SITE.phone1}
            </a>
            <a
              href={SITE.phone2Href}
              className="flex items-center gap-2 whitespace-nowrap text-base font-extrabold text-[#0F4C5C] hover:text-[#0284C7] transition-colors duration-200"
              data-testid="header-phone-link-diana"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EEF6FC]">
                <Phone className="h-4 w-4" aria-hidden="true" />
              </span>
              {t("header.dianaLabel")} {SITE.phone2}
            </a>
          </div>
          <Link
            to="/quote"
            className="hidden whitespace-nowrap bg-[#0F4C5C] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1A365D] sm:inline-block"
            data-testid="header-quote-btn"
          >
            {t("common.requestQuote")}
          </Link>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-[#0F4C5C] lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
            aria-label={t("header.mobileNav")}
            data-testid="mobile-nav"
          >
            <div className="space-y-1 px-4 py-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2.5 text-base font-semibold ${isActive ? "bg-[#EEF6FC] text-[#0F4C5C]" : "text-slate-700"}`
                  }
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t(`nav.${item.key}`)}
                </NavLink>
              ))}
              <Link
                to="/quote"
                className="mt-2 block bg-[#0F4C5C] px-3 py-3 text-center text-base font-bold text-white"
                data-testid="mobile-quote-btn"
              >
                {t("common.requestQuote")}
              </Link>
              <div className="pt-3">
                <LanguageToggle />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-[#0C2E3A] text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="font-heading mb-4 text-xl font-extrabold tracking-tight text-white" data-testid="footer-brand-wordmark">
              A&F Insurance Group, Inc.
            </p>
            <p className="text-sm leading-relaxed text-slate-400">{t("footer.blurb")}</p>
            <div className="mt-5 flex gap-3">
              <a href="https://www.facebook.com/AFInsuranceGroup/" target="_blank" rel="noopener noreferrer" aria-label="A&F Insurance Group on Facebook" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-[#D97706] transition-colors duration-200" data-testid="footer-facebook-link">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.linkedin.com/in/af-insurance-group-inc-83499140/" target="_blank" rel="noopener noreferrer" aria-label="A&F Insurance Group on LinkedIn" className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-[#D97706] transition-colors duration-200" data-testid="footer-linkedin-link">
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>
          <nav aria-label={t("footer.navAria")} data-testid="footer-nav">
            <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.pages")}</h2>
            <ul className="space-y-2.5 text-sm">
              {NAV.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="hover:text-white transition-colors duration-200">
                    {t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/accessibility-statement" className="hover:text-white transition-colors duration-200" data-testid="footer-accessibility-link">
                  {t("footer.accessibility")}
                </Link>
              </li>
              <li>
                <Link to="/quote" className="font-semibold text-[#E8A34C] hover:text-white transition-colors duration-200">
                  {t("common.requestQuote")}
                </Link>
              </li>
            </ul>
          </nav>
          <div>
            <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.contact")}</h2>
            <address className="space-y-3 text-sm not-italic leading-relaxed">
              <p>
                {SITE.address1}
                <br />
                {SITE.address2}
                <br />
                {SITE.address3}
              </p>
              <p>
                <a href={SITE.phone1Href} className="font-semibold text-white hover:text-[#E8A34C] transition-colors duration-200" data-testid="footer-phone-1">
                  {SITE.phone1}
                </a>
                <br />
                <a href={SITE.phone2Href} className="font-semibold text-white hover:text-[#E8A34C] transition-colors duration-200" data-testid="footer-phone-2">
                  {SITE.phone2}
                </a>
              </p>
              <p>
                <a href={`mailto:${SITE.email}`} className="hover:text-white transition-colors duration-200" data-testid="footer-email">
                  {SITE.email}
                </a>
              </p>
            </address>
          </div>
          <div>
            <h2 className="font-heading mb-4 text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.hoursTitle")}</h2>
            <p className="text-sm leading-relaxed">{t("common.hours")}</p>
            <Link
              to="/quote"
              className="mt-6 inline-block bg-[#D97706] px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#B45309]"
              data-testid="footer-quote-btn"
            >
              {t("common.getInsuranceQuote")}
            </Link>
          </div>
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} {SITE.name} {t("footer.rights")}</p>
          <p>{t("footer.tagline")}</p>
          <p data-testid="footer-design-credit">
            {t("footer.designedBy")}{" "}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-slate-300 underline decoration-[#D97706]/60 underline-offset-2 transition-colors duration-200 hover:text-[#E8A34C]"
              data-testid="footer-brand-partners-link"
            >
              Brand Partners
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function MobileCallBar() {
  const { t } = useLang();
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 border-t border-white/20 bg-[#0F4C5C] sm:hidden" data-testid="mobile-call-bar">
      <a href={SITE.phone1Href} className="flex items-center justify-center gap-2 py-3.5 text-sm font-bold text-white" data-testid="mobile-call-btn">
        <Phone className="h-4 w-4" aria-hidden="true" /> {t("common.callNow")}
      </a>
      <Link to="/quote" className="flex items-center justify-center gap-2 bg-[#D97706] py-3.5 text-sm font-bold text-white" data-testid="mobile-quote-bar-btn">
        {t("common.getQuote")}
      </Link>
    </div>
  );
}

export default function Layout() {
  return (
    <>
      <TopBar />
      <Header />
      <main id="main-content">
        <Outlet />
      </main>
      <Footer />
      <MobileCallBar />
      <AccessibilityWidget />
      <div className="h-[52px] sm:hidden" aria-hidden="true" />
    </>
  );
}
